/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  Directive,
  Host,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Renderer2,
  ElementRef,
  Input,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {SPACE, ENTER} from '@angular/material/core';
import {MdExpansionPanel, EXPANSION_PANEL_ANIMATION_TIMING} from './expansion-panel';
import {filter} from '@angular/material/core';
import {FocusMonitor} from '@angular/cdk/a11y';
import {merge} from 'rxjs/observable/merge';
import {Subscription} from 'rxjs/Subscription';
/**
 * <md-expansion-panel-header> component.
 * 
 * This component corresponds to the header element of an <md-expansion-panel>.
 * 
 * Please refer to README.md for examples on how to use it.
 */
export class MdExpansionPanelHeader implements OnDestroy {
private _parentChangeSubscription = Subscription.EMPTY;
/**
 * @param {?} renderer
 * @param {?} panel
 * @param {?} _element
 * @param {?} _focusMonitor
 * @param {?} _changeDetectorRef
 */
constructor(
    renderer: Renderer2,
public panel: MdExpansionPanel,
private _element: ElementRef,
private _focusMonitor: FocusMonitor,
private _changeDetectorRef: ChangeDetectorRef) {

    // Since the toggle state depends on an @Input on the panel, we
    // need to  subscribe and trigger change detection manually.
    this._parentChangeSubscription = merge(
      panel.opened,
      panel.closed,
      filter.call(panel._inputChanges, changes => !!(changes.hideToggle || changes.disabled))
    )
    .subscribe(() => this._changeDetectorRef.markForCheck());

    _focusMonitor.monitor(_element.nativeElement, renderer, false);
  }
/**
 * Height of the header while the panel is expanded.
 */
expandedHeight: string;
/**
 * Height of the header while the panel is collapsed.
 */
collapsedHeight: string;
/**
 * Toggles the expanded state of the panel.
 * @return {?}
 */
_toggle(): void {
    if (!this.panel.disabled) {
      this.panel.toggle();
    }
  }
/**
 * Gets whether the panel is expanded.
 * @return {?}
 */
_isExpanded(): boolean {
    return this.panel.expanded;
  }
/**
 * Gets the expanded state string of the panel.
 * @return {?}
 */
_getExpandedState(): string {
    return this.panel._getExpandedState();
  }
/**
 * Gets the panel id.
 * @return {?}
 */
_getPanelId(): string {
    return this.panel.id;
  }
/**
 * Gets whether the expand indicator should be shown.
 * @return {?}
 */
_showToggle(): boolean {
    return !this.panel.hideToggle && !this.panel.disabled;
  }
/**
 * Handle keyup event calling to toggle() if appropriate.
 * @param {?} event
 * @return {?}
 */
_keyup(event: KeyboardEvent) {
    switch (event.keyCode) {
      // Toggle for space and enter keys.
      case SPACE:
      case ENTER:
        event.preventDefault();
        this._toggle();
        break;
      default:
        return;
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._parentChangeSubscription.unsubscribe();
    this._focusMonitor.stopMonitoring(this._element.nativeElement);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-expansion-panel-header, mat-expansion-panel-header',
  styleUrls: ['./expansion-panel-header.css'],
  templateUrl: './expansion-panel-header.html',
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'mat-expansion-panel-header',
    'role': 'button',
    '[attr.tabindex]': 'panel.disabled ? -1 : 0',
    '[attr.aria-controls]': '_getPanelId()',
    '[attr.aria-expanded]': '_isExpanded()',
    '[attr.aria-disabled]': 'panel.disabled',
    '[class.mat-expanded]': '_isExpanded()',
    '(click)': '_toggle()',
    '(keyup)': '_keyup($event)',
    '[@expansionHeight]': `{
        value: _getExpandedState(),
        params: {
          collapsedHeight: collapsedHeight,
          expandedHeight: expandedHeight
        }
    }`,
  },
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({transform: 'rotate(0deg)'})),
      state('expanded', style({transform: 'rotate(180deg)'})),
      transition('expanded <=> collapsed', animate(EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
    trigger('expansionHeight', [
      state('collapsed', style({
        height: '{{collapsedHeight}}',
      }), {
        params: {collapsedHeight: '48px'},
      }),
      state('expanded', style({
        height: '{{expandedHeight}}'
      }), {
        params: {expandedHeight: '64px'}
      }),
      transition('expanded <=> collapsed', animate(EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
  ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: MdExpansionPanel, decorators: [{ type: Host }, ]},
{type: ElementRef, },
{type: FocusMonitor, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'expandedHeight': [{ type: Input },],
'collapsedHeight': [{ type: Input },],
};
}

function MdExpansionPanelHeader_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelHeader.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelHeader.ctorParameters;
/** @type {?} */
MdExpansionPanelHeader.propDecorators;
/** @type {?} */
MdExpansionPanelHeader.prototype._parentChangeSubscription;
/**
 * Height of the header while the panel is expanded.
 * @type {?}
 */
MdExpansionPanelHeader.prototype.expandedHeight;
/**
 * Height of the header while the panel is collapsed.
 * @type {?}
 */
MdExpansionPanelHeader.prototype.collapsedHeight;
/** @type {?} */
MdExpansionPanelHeader.prototype.panel;
/** @type {?} */
MdExpansionPanelHeader.prototype._element;
/** @type {?} */
MdExpansionPanelHeader.prototype._focusMonitor;
/** @type {?} */
MdExpansionPanelHeader.prototype._changeDetectorRef;
}

/**
 * <md-panel-description> directive.
 * 
 * This direction is to be used inside of the MdExpansionPanelHeader component.
 */
export class MdExpansionPanelDescription {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-panel-description, mat-panel-description',
  host : {
    class: 'mat-expansion-panel-header-description'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdExpansionPanelDescription_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelDescription.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelDescription.ctorParameters;
}

/**
 * <md-panel-title> directive.
 * 
 * This direction is to be used inside of the MdExpansionPanelHeader component.
 */
export class MdExpansionPanelTitle {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-panel-title, mat-panel-title',
  host : {
    class: 'mat-expansion-panel-header-title'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdExpansionPanelTitle_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelTitle.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelTitle.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
